import * as angular from 'angular';

import {
    ActualDataType,
    ComponentDefinition,
    Dictionary,
    DigitalSet,
    DigitalState,
    EditSpotValue,
    IDateRangePickerOptions,
    TagDefinition
} from '../../models';
import { IFormatNumberHelper, ISpotValueHelperService } from '../../services';
import { EditSpotValueWithOptions } from '../../models/EditSpotValueWithOptions';

class SpotValuesReadAndEditTableController implements angular.IComponentController {
    dateOptions: IDateRangePickerOptions;
    digitalSet: DigitalSet;
    digitalStateCollection: DigitalState[];
    digitalStatesDictionary: { [id: number]: string };
    formController: angular.IFormController;
    isDigital: boolean;
    valuesWithOptions: EditSpotValueWithOptions[];

    readonly decimals: number = 2;
    readonly digitalSets: Dictionary<DigitalSet>;
    readonly isEditMode: boolean;
    readonly isLoading: boolean;
    readonly onDeleteRow?: ({ spotValue: SpotValue, index: number }) => void;
    readonly onValueChanged?: ({ spotValue: SpotValue }) => void;
    readonly selected: TagDefinition;
    readonly values: EditSpotValue[];

    static $inject = [
        '$translate',
        'spotValueHelperService',
        'formatNumberHelper'
    ];

    constructor(
        private _$translate: angular.translate.ITranslateService,
        private _spotValueHelperService: ISpotValueHelperService,
        private _formatNumberHelper: IFormatNumberHelper
    ) {
    }

    $onInit() {
        this.digitalStatesDictionary = {};
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['isEditMode']) {
            if (this.isEditMode === false) {
                this.setPristine();
            }
        }
        if (changesObj['selected'] && this.selected) {
            this.isDigital = this.selected.actualDataType === ActualDataType.Digital;
            if (this.isDigital) {
                this.digitalSet = this.digitalSets[this.selected.digitalSetGuid];
                this.digitalStateCollection = [{ code: null, name: '-' }]
                    .concat(this.digitalSet.stateCollection.map(d => d));
                const dic = {};
                this.digitalSet.stateCollection.forEach(s => dic[s.code] = s.name);
                this.digitalStatesDictionary = dic;
            } else {
                this.digitalSet = null;
                this.digitalStatesDictionary = {};
                this.digitalStateCollection = null;
            }
        }
        if (changesObj['values'] && this.values) {
            // Hack to be able to have a different callback for each row when date is changed:
            //  each row has a separate options object
            // This is done here to set the form dirty and trigger the same chage mechanism as the other fields
            this.valuesWithOptions = this.values.map((v, i) => {
                try {
                    return Object.assign(v, { options: this.getOptions(v, i), title: this.getTitle(v) });
                } catch (e) {
                    console.error(e);
                }
            });
        }
    }

    onChange(spotValue: EditSpotValueWithOptions, $index: number) {
        if (this.onValueChanged) {
            spotValue.title = this.getTitle(spotValue);
            this.onValueChanged({ spotValue });
        }
    }

    onTimestampChange(spotValue: EditSpotValueWithOptions, $index: number) {
        if (this.formController && this.formController['timestamp' + $index]) {
            this.formController['timestamp' + $index].$setDirty();
            const dates = this.valuesWithOptions
                .map(v => v.momentTimestamp.toISOString());
            const newDate = spotValue.momentTimestamp.toISOString();
            const matching = dates.filter(d => d === newDate);
            const hasDuplicate = matching.length > 1;
            this.formController['timestamp' + $index].$setValidity('duplicate', !hasDuplicate);
        }
        this.onChange(spotValue, $index);
    }

    deleteRow(spotValue: EditSpotValueWithOptions, $index: number) {
        if (this.onDeleteRow) {
            this.onDeleteRow({ spotValue, index: $index });
        }
        if (this.formController) {
            this.formController.$setDirty();
        }
    }

    private getOptions(spotValue: EditSpotValue, $index: number): IDateRangePickerOptions {
        const dateOptions = this._spotValueHelperService.getDateOptions();
        const eventHandlers = dateOptions.eventHandlers || {};
        const origCallback = eventHandlers['apply.daterangepicker'];
        eventHandlers['apply.daterangepicker'] = (evt, picker) => {
            if (origCallback) {
                origCallback(evt, picker);
            }
            this.onTimestampChange(spotValue as EditSpotValueWithOptions, $index);
        };
        dateOptions.eventHandlers = eventHandlers;
        return dateOptions;
    }

    private getTitle(spotValue: EditSpotValue): string {
        return spotValue.logbookEntryId
            ? this._$translate.instant('Readonly because value is linked to a logbook entry')
            : this.isDigital
                ? this.digitalStatesDictionary[spotValue.value] || '-'
                : this._formatNumberHelper.format(spotValue.value, this.decimals);
    }

    private setPristine() {
        this.formController.$setPristine();
    }
}

export const wtSpotValuesReadAndEditTable: ComponentDefinition = {
    componentId: 'wtSpotValuesReadAndEditTable',
    controller: SpotValuesReadAndEditTableController,
    template: require('./spotValuesReadAndEditTable.component.html'),
    controllerAs: 'vm',
    bindings: {
        digitalSets: '<',
        formController: '=?',
        isEditMode: '<',
        isLoading: '<?',
        onDeleteRow: '&?',
        onValueChanged: '&?',
        selected: '<',
        values: '<'
    }
};